// src/components/dashboard/SignedContractsTable.jsx
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { supabase } from '../../lib/supabaseClient';
import {
  FiSearch, FiFilter, FiChevronUp, FiChevronDown, FiChevronRight,
  FiEye, FiPlusCircle, FiFileText, FiLoader, FiDownload, FiSlash,
} from 'react-icons/fi';
import { ouvrirDocumentStocke } from '../../utils/documentStocke';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatPeriodLabel(period) {
  if (!period) return '';
  const MOIS = ['Janvier','Février','Mars','Avril','Mai','Juin',
                'Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
  if (period.type === 'month' && period.year && period.month)
    return `${MOIS[period.month - 1]} ${period.year}`;
  if (period.type === 'year' && period.year) return `Année ${period.year}`;
  if (period.type === 'custom' && period.startDate && period.endDate)
    return `${new Date(period.startDate).toLocaleDateString('fr-FR')} – ${new Date(period.endDate).toLocaleDateString('fr-FR')}`;
  return '';
}

function parseMetadata(raw) {
  if (!raw) return {};
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw; }
  catch { return {}; }
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function resolveCompanyName(contract) {
  return (
    contract?.account?.company_name ||
    parseMetadata(contract?.metadata).company_name ||
    null
  );
}

function resolveInitials(contract) {
  const name = resolveCompanyName(contract);
  if (!name) return '?';
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

// ─── StatusIndicator ──────────────────────────────────────────────────────────
const DOT_STYLES = {
  inactive: 'bg-gray-200 border-gray-300',
  signed:   'bg-orange-400 border-orange-500',
  closed:   'bg-teal-400 border-teal-500',
};

const StatusIndicator = ({ status = 'inactive', title }) => (
  <span
    title={title}
    className={`inline-block w-2.5 h-2.5 rounded-full border transition-colors ${
      DOT_STYLES[status] || DOT_STYLES.inactive
    }`}
  />
);

// ─── TaxesStageBadge ──────────────────────────────────────────────────────────
const TAXES_STAGE_CONFIG = {
  en_cours: {
    label: 'En cours',
    className: 'bg-amber-100 text-amber-800 border border-amber-200',
    dot: 'bg-amber-400',
  },
  envoye: {
    label: 'Envoyé',
    className: 'bg-blue-100 text-blue-800 border border-blue-200',
    dot: 'bg-blue-400',
  },
  signe: {
    label: 'Signé',
    className: 'bg-green-100 text-green-800 border border-green-200',
    dot: 'bg-green-400',
  },
};

const TaxesStageBadge = ({ stage }) => {
  const cfg = TAXES_STAGE_CONFIG[stage];
  if (!cfg) return <span className="text-[9px] text-gray-300">—</span>;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium ${cfg.className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

// ─── Logique statut taxes ─────────────────────────────────────────────────────
function getTaxesStageForYear(accountId, fiscal_year, dossierByAccount, eraByAccount) {
  const dossier = (dossierByAccount[accountId] || []).find(d => d.fiscal_year === fiscal_year);
  if (dossier?.dossier_stage === 'signe')                return 'signe';
  if (dossier?.dossier_stage === 'en_attente_signature') return 'envoye';
  const hasERA = (eraByAccount[accountId] || []).some(c => c.fiscal_year === fiscal_year);
  if (hasERA || dossier)                                 return 'en_cours';
  return null;
}

function getActiveYearsForAccount(accountId, dossierByAccount, eraByAccount) {
  const yearsSet = new Set();
  (dossierByAccount[accountId] || []).forEach(d => yearsSet.add(d.fiscal_year));
  (eraByAccount[accountId]     || []).forEach(c => yearsSet.add(c.fiscal_year));
  return [...yearsSet].sort((a, b) => b - a);
}

// ─── Composant principal ──────────────────────────────────────────────────────
const SignedContractsTable = ({
  contracts = [],
  selectedPeriod,
  session,
  existingOppIds = {},
}) => {
  const navigate = useNavigate();

  const [opportunityIds,   setOpportunityIds]   = useState(existingOppIds);
  const [showFilters,      setShowFilters]       = useState(false);
  const [filters, setFilters] = useState({
    search: '', commercial: '', courtage: '', achem: '', cspe: '', ticgn: '',
  });
  const [sortConfig,       setSortConfig]        = useState({ key: 'signed_at', direction: 'desc' });
  const [expandedRows,     setExpandedRows]      = useState(new Set());
  const [dossierByAccount, setDossierByAccount]  = useState({});
  const [eraByAccount,     setEraByAccount]      = useState({});
  const [loadingMissions,  setLoadingMissions]   = useState(false);
  const [downloadingKey,   setDownloadingKey]    = useState(null);
  // ✅ Inéligibilité
  const [ineligibleIds,    setIneligibleIds]     = useState(new Set());
  const [togglingId,       setTogglingId]        = useState(null);

  useEffect(() => { setOpportunityIds(existingOppIds); }, [existingOppIds]);

  // ✅ Initialiser ineligibleIds depuis les données contracts
  useEffect(() => {
    const ids = new Set(
      contracts
        .filter(c => c.account?.cspe_ineligible === true)
        .map(c => c.account_id)
    );
    setIneligibleIds(ids);
  }, [contracts]);

  // ── Fetch missions ──
  useEffect(() => {
    if (!contracts.length) return;
    const accountIds = [...new Set(contracts.map(c => c.account_id).filter(Boolean))];
    if (!accountIds.length) return;

    const fetchMissionsData = async () => {
      setLoadingMissions(true);
      try {
        const [dossierRes, eraRes] = await Promise.all([
          supabase
            .from('cspe_dossier_years')
            .select('account_id, fiscal_year, dossier_stage, cerfa_signed_at, signed_storage_path')
            .in('account_id', accountIds),
          supabase
            .from('generated_cerfa_documents')
            .select('account_id, fiscal_year')
            .in('account_id', accountIds)
            .eq('document_type', 'CERFA_ERA'),
        ]);

        const byAccountDossier = {};
        (dossierRes.data || []).forEach(d => {
          if (!byAccountDossier[d.account_id]) byAccountDossier[d.account_id] = [];
          byAccountDossier[d.account_id].push(d);
        });

        const byAccountEra = {};
        (eraRes.data || []).forEach(c => {
          if (!byAccountEra[c.account_id]) byAccountEra[c.account_id] = [];
          byAccountEra[c.account_id].push(c);
        });

        setDossierByAccount(byAccountDossier);
        setEraByAccount(byAccountEra);
      } catch (err) {
        console.error('[SignedContractsTable] fetchMissionsData error:', err);
      } finally {
        setLoadingMissions(false);
      }
    };

    fetchMissionsData();
  }, [contracts]);

  // ── Téléchargement CERFA signé ──
  const handleDownloadSignedCerfa = useCallback(async (accountId, fiscal_year, storagePath) => {
    const key = `${accountId}-${fiscal_year}`;
    if (downloadingKey === key) return;
    setDownloadingKey(key);
    try {
      const { data, error } = await supabase.storage
        .from('cerfa-bundles')
        .createSignedUrl(storagePath, 3600);
      if (error || !data?.signedUrl) throw new Error(error?.message || 'URL introuvable');
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('[Download CERFA signé]', err);
      toast.error(`Impossible de télécharger le CERFA ${fiscal_year} : ${err.message}`);
    } finally {
      setDownloadingKey(null);
    }
  }, [downloadingKey]);

  // ✅ Basculer l'éligibilité CSPE
  const handleToggleEligibility = useCallback(async (accountId, currentlyIneligible) => {
    if (togglingId === accountId) return;
    setTogglingId(accountId);
    try {
      const newValue = !currentlyIneligible;
      const { error } = await supabase
        .from('accounts')
        .update({ cspe_ineligible: newValue })
        .eq('id', accountId);
      if (error) throw error;

      setIneligibleIds(prev => {
        const next = new Set(prev);
        newValue ? next.add(accountId) : next.delete(accountId);
        return next;
      });

      toast.success(
        newValue ? 'Client marqué comme inéligible' : 'Client marqué comme éligible',
        { duration: 2000 }
      );
    } catch (err) {
      toast.error(`Erreur : ${err.message}`);
    } finally {
      setTogglingId(null);
    }
  }, [togglingId]);

  const toggleRow = useCallback((contractId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(contractId) ? next.delete(contractId) : next.add(contractId);
      return next;
    });
  }, []);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const SortableHeader = ({ label, colKey, className = '' }) => (
    <th
      onClick={() => handleSort(colKey)}
      className={`px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 ${className}`}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortConfig.key === colKey ? (
          sortConfig.direction === 'asc'
            ? <FiChevronUp   className="w-3 h-3 text-teal-600" />
            : <FiChevronDown className="w-3 h-3 text-teal-600" />
        ) : (
          <FiChevronDown className="w-3 h-3 text-gray-300" />
        )}
      </span>
    </th>
  );

  const filteredAndSorted = useMemo(() => {
    let data = [...contracts];
    const { search, commercial, courtage, achem, cspe, ticgn } = filters;

    if (search) {
      const q = search.toLowerCase();
      data = data.filter(c => {
        const name = resolveCompanyName(c) || '';
        const meta = parseMetadata(c.metadata);
        return (
          name.toLowerCase().includes(q) ||
          (meta.siret || '').toLowerCase().includes(q) ||
          (c.id       || '').toLowerCase().includes(q)
        );
      });
    }
    if (commercial) {
      data = data.filter(c =>
        (c.owner?.full_name || '').toLowerCase().includes(commercial.toLowerCase())
      );
    }
    if (courtage) {
      data = data.filter(c => {
        const active = !!parseMetadata(c.metadata).courtage;
        return courtage === 'oui' ? active : !active;
      });
    }
    if (achem) {
      data = data.filter(c => {
        const active = !!parseMetadata(c.metadata).acheminement;
        return achem === 'oui' ? active : !active;
      });
    }
    if (cspe) {
      data = data.filter(c => {
        const st = c.tax_contract_subtype || parseMetadata(c.metadata).taxContractType || 'CSPE';
        const has = st === 'CSPE' || st === 'mixte';
        return cspe === 'oui' ? has : !has;
      });
    }
    if (ticgn) {
      data = data.filter(c => {
        const st = c.tax_contract_subtype || parseMetadata(c.metadata).taxContractType || 'CSPE';
        const has = st === 'TICGN' || st === 'mixte';
        return ticgn === 'oui' ? has : !has;
      });
    }

    data.sort((a, b) => {
      let av = sortConfig.key === 'company_name' ? resolveCompanyName(a) || '' : a[sortConfig.key];
      let bv = sortConfig.key === 'company_name' ? resolveCompanyName(b) || '' : b[sortConfig.key];
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = String(av).localeCompare(String(bv), 'fr', { numeric: true });
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });

    return data;
  }, [contracts, filters, sortConfig]);

  const handleCreateOpportunity = (contract) => {
    const companyName = resolveCompanyName(contract) || 'Client inconnu';
    toast(
      (t) => (
        <div className="text-xs">
          <p className="font-semibold mb-1">Créer une opportunité pour</p>
          <p className="text-gray-600 mb-2 font-medium">{companyName} ?</p>
          <div className="flex gap-2">
            <button
              onClick={() => { toast.dismiss(t.id); _doCreate(contract, companyName); }}
              className="px-2 py-1 bg-teal-600 text-white rounded text-[10px] hover:bg-teal-700"
            >Confirmer</button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-[10px] hover:bg-gray-300"
            >Annuler</button>
          </div>
        </div>
      ),
      { duration: 8000 }
    );
  };

  const _doCreate = async (contract, companyName) => {
    try {
      const year = selectedPeriod?.year || new Date().getFullYear();
      const { data } = await axios.post(
        '/api/opportunities',
        {
          account_id:       contract.account_id,
          opportunity_name: `Cotation - ${companyName}`,
          crm_status:       'Nouveau',
          year,
        },
        { headers: { Authorization: `Bearer ${session?.access_token}` } }
      );
      const newId = data?.data?.id || data?.id;
      if (newId) {
        setOpportunityIds(prev => ({ ...prev, [contract.id]: newId }));
        toast.success(`Opportunité créée — ${companyName}`, { duration: 3000 });
      }
    } catch (err) {
      console.error('[SignedContractsTable] Erreur création :', err);
      toast.error('Erreur lors de la création');
    }
  };

  const periodLabel        = formatPeriodLabel(selectedPeriod);
  const activeFiltersCount = Object.values(filters).filter(Boolean).length;
  const TOTAL_COLS         = 10;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">

      {/* En-tête */}
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-xs font-semibold text-gray-800 flex items-center gap-1.5">
            Contrats signés
            {periodLabel && (
              <span className="px-1.5 py-0.5 bg-teal-50 text-teal-700 rounded text-[9px] font-medium">
                {periodLabel}
              </span>
            )}
            {activeFiltersCount > 0 && (
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px]">
                {activeFiltersCount} filtre{activeFiltersCount > 1 ? 's' : ''}
              </span>
            )}
            {loadingMissions && <FiLoader className="w-3 h-3 animate-spin text-gray-400" />}
          </h2>
          <p className="text-[9px] text-gray-400 mt-0.5">
            {filteredAndSorted.length} contrat{filteredAndSorted.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="flex items-center gap-1.5">
          <div className="relative">
            <FiSearch className="absolute left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher…"
              value={filters.search}
              onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
              className="pl-5 pr-2 py-1 border border-gray-200 rounded text-[10px] w-32 focus:outline-none focus:ring-1 focus:ring-teal-400"
            />
          </div>
          <button
            onClick={() => setShowFilters(p => !p)}
            className={`px-1.5 py-1 rounded text-[10px] border flex items-center gap-1 transition-colors ${
              showFilters
                ? 'bg-teal-50 border-teal-300 text-teal-700'
                : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
            }`}
          >
            <FiFilter className="w-2.5 h-2.5" />Filtres
          </button>
          {activeFiltersCount > 0 && (
            <button
              onClick={() => setFilters({ search: '', commercial: '', courtage: '', achem: '', cspe: '', ticgn: '' })}
              className="px-1.5 py-1 text-[10px] text-gray-500 hover:text-red-500 border border-gray-200 rounded"
            >✕ Reset</button>
          )}
        </div>
      </div>

      {/* Filtres avancés */}
      {showFilters && (
        <div className="px-2 py-1 bg-gray-50 border-b border-gray-100 flex flex-wrap gap-1.5 items-center">
          <input
            placeholder="Commercial…"
            value={filters.commercial}
            onChange={e => setFilters(p => ({ ...p, commercial: e.target.value }))}
            className="py-0.5 px-1.5 border border-gray-200 rounded text-[10px] w-28 focus:outline-none focus:ring-1 focus:ring-teal-300"
          />
          {[
            { key: 'courtage', label: 'Courtage' },
            { key: 'achem',    label: 'Achem.'   },
            { key: 'cspe',     label: 'CSPE'     },
            { key: 'ticgn',    label: 'TICGN'    },
          ].map(({ key, label }) => (
            <select
              key={key}
              value={filters[key]}
              onChange={e => setFilters(p => ({ ...p, [key]: e.target.value }))}
              className="py-0.5 px-1 border border-gray-200 rounded text-[10px] focus:outline-none"
            >
              <option value="">{label}</option>
              <option value="oui">Oui</option>
              <option value="non">Non</option>
            </select>
          ))}
        </div>
      )}

      {/* Tableau */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-1 py-1.5 w-6" />
              <SortableHeader label="Client"     colKey="company_name" />
              <SortableHeader label="Commercial" colKey="owner_name"   />
              <SortableHeader label="Signé le"   colKey="signed_at"    />
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider bg-teal-50  text-teal-700">Courtage</th>
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider bg-violet-50 text-violet-700">Achem.</th>
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700">CSPE</th>
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider bg-orange-50 text-orange-700">TICGN</th>
              <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase">Doc.</th>
              <th className="px-2 py-1.5 text-center text-[10px] font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {filteredAndSorted.length === 0 ? (
              <tr>
                <td colSpan={TOTAL_COLS} className="px-3 py-4 text-center text-[10px] text-gray-400">
                  Aucun contrat signé pour cette période
                </td>
              </tr>
            ) : (
              filteredAndSorted.map(contract => {
                const meta       = parseMetadata(contract.metadata);
                const oppId      = opportunityIds[contract.id];
                const accountId  = contract.account_id;
                const isExpanded = expandedRows.has(contract.id);

                // ✅ Inéligibilité
                const isIneligible = ineligibleIds.has(accountId);
                const isToggling   = togglingId === accountId;

                const accountStatus = contract.account?.status || '';
                const isClosed = ['clôturé','closed','inactif','inactive'].includes(
                  accountStatus.toLowerCase()
                );

                const subtype = contract.tax_contract_subtype || meta.taxContractType || 'CSPE';
                const hasCspe  = subtype === 'CSPE' || subtype === 'mixte';
                const hasTicgn = subtype === 'TICGN' || subtype === 'mixte';

                const cspeStatus     = hasCspe  ? (isClosed ? 'closed' : 'signed') : 'inactive';
                const ticgnStatus    = hasTicgn ? (isClosed ? 'closed' : 'signed') : 'inactive';
                const courtageStatus = meta.courtage     ? (isClosed ? 'closed' : 'signed') : 'inactive';
                const achemStatus    = meta.acheminement ? (isClosed ? 'closed' : 'signed') : 'inactive';

                const companyName = resolveCompanyName(contract);
                const siret       = contract.account?.siret || meta.siret || '';
                const activeYears = getActiveYearsForAccount(accountId, dossierByAccount, eraByAccount);
                const hasActivity = activeYears.length > 0;

                return (
                  <React.Fragment key={contract.id}>

                    {/* ✅ Ligne principale — grisée si inéligible */}
                    <tr className={`transition-colors ${
                      isIneligible
                        ? 'bg-gray-100 opacity-50'
                        : isExpanded ? 'bg-orange-50/30' : 'hover:bg-gray-50'
                    }`}>

                      {/* Expand */}
                      <td className="px-1 py-1.5 text-center">
                        <button
                          onClick={() => toggleRow(contract.id)}
                          disabled={!hasActivity && !loadingMissions}
                          title={hasActivity ? 'Voir le détail par année' : 'Aucune mission CSPE enregistrée'}
                          className={`inline-flex items-center justify-center w-5 h-5 rounded transition-colors ${
                            hasActivity
                              ? isExpanded
                                ? 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                              : 'text-gray-200 cursor-default'
                          }`}
                        >
                          {isExpanded
                            ? <FiChevronDown  className="w-3 h-3" />
                            : <FiChevronRight className="w-3 h-3" />
                          }
                        </button>
                      </td>

                      {/* Client */}
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[9px] font-semibold flex-shrink-0 ${
                            isIneligible
                              ? 'bg-gray-200 text-gray-400'
                              : 'bg-teal-100 text-teal-700'
                          }`}>
                            {resolveInitials(contract)}
                          </span>
                          <div>
                            <p className={`text-[10px] font-medium leading-tight flex items-center gap-1 ${
                              isIneligible ? 'text-gray-400 line-through' : 'text-gray-800'
                            }`}>
                              {companyName ?? (
                                <span className="text-gray-400 italic">#{accountId?.slice(0,8)}</span>
                              )}
                              {/* ✅ Badge inéligible */}
                              {isIneligible && (
                                <span className="px-1 py-0.5 bg-red-100 text-red-500 rounded text-[8px] font-semibold no-underline" style={{ textDecoration: 'none' }}>
                                  Inéligible
                                </span>
                              )}
                            </p>
                            {siret && <p className="text-[9px] text-gray-400">{siret}</p>}
                          </div>
                        </div>
                      </td>

                      {/* Commercial */}
                      <td className="px-2 py-1.5">
                        {contract.account?.owner?.full_name
                          ? <span className="text-[10px] text-gray-700 font-medium">{contract.account.owner.full_name}</span>
                          : <span className="text-[9px] text-gray-300 italic">—</span>
                        }
                      </td>

                      {/* Signé le */}
                      <td className="px-2 py-1.5 text-[10px] text-gray-600">
                        {formatDate(contract.signed_at)}
                      </td>

                      {/* Courtage */}
                      <td className="px-2 py-1.5 bg-teal-50/30">
                        <StatusIndicator status={courtageStatus} title={
                          courtageStatus === 'signed' ? 'Courtage — actif' :
                          courtageStatus === 'closed' ? 'Courtage — clôturé' :
                          'Courtage — non souscrit'
                        } />
                      </td>

                      {/* Achem */}
                      <td className="px-2 py-1.5 bg-violet-50/30">
                        <StatusIndicator status={achemStatus} title={
                          achemStatus === 'signed' ? 'Acheminement — actif' :
                          achemStatus === 'closed' ? 'Acheminement — clôturé' :
                          'Acheminement — non souscrit'
                        } />
                      </td>

                      {/* CSPE */}
                      <td className="px-2 py-1.5 bg-amber-50/30">
                        <StatusIndicator status={cspeStatus} title={
                          cspeStatus === 'closed' ? 'CSPE — compte clôturé' : 'CSPE — contrat signé'
                        } />
                      </td>

                      {/* TICGN */}
                      <td className="px-2 py-1.5 bg-orange-50/30">
                        <StatusIndicator status={ticgnStatus} title={
                          ticgnStatus === 'closed' ? 'TICGN — compte clôturé' : 'TICGN — contrat signé'
                        } />
                      </td>

                      {/* Doc */}
                      <td className="px-2 py-1.5">
                        {contract.signed_document_url ? (
                          <a
                            href={contract.signed_document_url}
                            onClick={(e) => { e.preventDefault(); ouvrirDocumentStocke(contract.signed_document_url); }}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center w-6 h-6 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                            title="Voir le document signé"
                          >
                            <FiFileText className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="text-[9px] text-gray-300">—</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-2 py-1.5">
                        <div className="flex items-center justify-center gap-1">

                          {/* ✅ Bouton inéligibilité */}
                          <button
                            onClick={() => handleToggleEligibility(accountId, isIneligible)}
                            disabled={isToggling}
                            title={isIneligible ? 'Marquer comme éligible' : 'Marquer comme inéligible'}
                            className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors ${
                              isToggling
                                ? 'bg-gray-100 text-gray-300 cursor-wait'
                                : isIneligible
                                  ? 'bg-red-100 text-red-500 hover:bg-red-200 cursor-pointer'
                                  : 'bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-400 cursor-pointer'
                            }`}
                          >
                            {isToggling
                              ? <FiLoader className="w-3 h-3 animate-spin" />
                              : <FiSlash  className="w-3.5 h-3.5" />
                            }
                          </button>

                          {/* Créer opportunité */}
                          <button
                            onClick={() => !oppId && handleCreateOpportunity(contract)}
                            disabled={!!oppId}
                            title={oppId ? 'Opportunité déjà créée' : 'Créer une opportunité'}
                            className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors ${
                              oppId
                                ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                                : 'bg-teal-50 text-teal-600 hover:bg-teal-100 cursor-pointer'
                            }`}
                          >
                            <FiPlusCircle className="w-3.5 h-3.5" />
                          </button>

                          {/* Voir opportunité */}
                          <button
                            onClick={() => oppId && navigate(`/dashboard/opportunities/${oppId}`)}
                            disabled={!oppId}
                            title={oppId ? "Voir l'opportunité" : 'Aucune opportunité'}
                            className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors ${
                              oppId
                                ? 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 cursor-pointer'
                                : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                            }`}
                          >
                            <FiEye className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* ── Sous-ligne déroulante ── */}
                    {isExpanded && (
                      <tr className="bg-orange-50/20">
                        <td colSpan={TOTAL_COLS} className="px-4 py-2">
                          <div className="border border-orange-100 rounded-md overflow-hidden">
                            <div className="grid grid-cols-5 bg-orange-50 border-b border-orange-100 px-3 py-1">
                              <span className="text-[9px] font-semibold text-orange-700 uppercase tracking-wider">Année</span>
                              <span className="text-[9px] font-semibold text-orange-700 uppercase tracking-wider">Taxes CSPE</span>
                              <span className="text-[9px] font-semibold text-green-700  uppercase tracking-wider">Signé le</span>
                              <span className="text-[9px] font-semibold text-gray-500   uppercase tracking-wider">CERFA signé</span>
                              <span className="text-[9px] font-semibold text-teal-600   uppercase tracking-wider">Courtage / Achem.</span>
                            </div>

                            {activeYears.length === 0 ? (
                              <div className="px-3 py-2 text-[9px] text-gray-400 italic">
                                Aucune mission enregistrée
                              </div>
                            ) : (
                              activeYears.map(year => {
                                const taxesStage = getTaxesStageForYear(
                                  accountId, year, dossierByAccount, eraByAccount
                                );
                                const dossier     = (dossierByAccount[accountId] || []).find(d => d.fiscal_year === year);
                                const signedAt    = dossier?.cerfa_signed_at    || null;
                                const storagePath = dossier?.signed_storage_path || null;
                                const dlKey       = `${accountId}-${year}`;
                                const isDownloading = downloadingKey === dlKey;

                                return (
                                  <div
                                    key={year}
                                    className="grid grid-cols-5 px-3 py-1.5 border-b border-orange-50 last:border-0 hover:bg-white/60 transition-colors items-center"
                                  >
                                    <span className="text-[10px] font-bold text-gray-700">{year}</span>
                                    <span className="flex items-center"><TaxesStageBadge stage={taxesStage} /></span>
                                    <span className="text-[9px] text-gray-600">
                                      {taxesStage === 'signe' && signedAt
                                        ? formatDate(signedAt)
                                        : <span className="text-gray-300">—</span>
                                      }
                                    </span>
                                    <span className="flex items-center">
                                      {taxesStage === 'signe' && storagePath ? (
                                        <button
                                          onClick={() => handleDownloadSignedCerfa(accountId, year, storagePath)}
                                          disabled={isDownloading}
                                          title={`Télécharger le CERFA signé ${year}`}
                                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors ${
                                            isDownloading
                                              ? 'bg-gray-100 text-gray-400 cursor-wait'
                                              : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 cursor-pointer'
                                          }`}
                                        >
                                          {isDownloading
                                            ? <FiLoader   className="w-2.5 h-2.5 animate-spin" />
                                            : <FiDownload className="w-2.5 h-2.5" />
                                          }
                                          {isDownloading ? 'Chargement…' : 'Télécharger'}
                                        </button>
                                      ) : (
                                        <span className="text-[9px] text-gray-300">—</span>
                                      )}
                                    </span>
                                    <span className="flex items-center text-[9px] text-gray-300 italic">À venir</span>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SignedContractsTable;
